/* CR "Ver más piezas" – carga incremental de la colección/búsqueda.
 * - Pide la siguiente página con Section Rendering API (?page=N&section_id=…) conservando filtros/orden.
 * - Agrega las tarjetas tal cual las renderiza el tema (mismo snippet product-card).
 * - Actualiza ?page=N con history.replaceState.
 * - Regreso desde ficha: si la URL trae page=N>1 se re-cargan las páginas 1..N-1 antes de las
 *   tarjetas del servidor y se restaura el scroll a la pieza clicada (sessionStorage).
 */
(() => {
  if (customElements.get('cr-ver-mas')) return;
  const GRID = '.\\#grid';
  const STORE = 'crVerMas';

  const keyFor = (u) => { const x = new URL(u, location.origin); x.searchParams.delete('page'); x.searchParams.delete('section_id'); return x.pathname + '?' + x.searchParams.toString(); };

  class CrVerMas extends HTMLElement {
    connectedCallback() {
      if (this._init) return; this._init = true;
      this.sectionId = this.getAttribute('section-id');
      this.page = +this.dataset.page; this.pages = +this.dataset.pages;
      this.per = +this.dataset.per; this.total = +this.dataset.total;
      this.firstPage = this.page;
      this.wrap = this.parentElement;
      this.grid = this.wrap && this.wrap.querySelector(GRID);
      this.btn = this.querySelector('[data-vm-btn]');
      this.text = this.querySelector('[data-vm-text]');
      this.bar = this.querySelector('[data-vm-bar]');
      if (!this.grid) return;
      this.cards = () => [...this.grid.children].filter(c => !c.classList.contains('cr-vm-sep'));
      if (this.btn) this.btn.addEventListener('click', e => { e.preventDefault(); this.loadNext(); });
      this.grid.addEventListener('click', e => this._remember(e), true);
      if (this.firstPage > 1) this._restore();
    }

    _url(page, section) {
      const u = new URL(location.href);
      u.searchParams.delete('section_id');
      if (page > 1) u.searchParams.set('page', page); else u.searchParams.delete('page');
      if (section) u.searchParams.set('section_id', this.sectionId);
      return u;
    }

    async _fetchCards(page) {
      const res = await fetch(this._url(page, true).toString(), { credentials: 'same-origin' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const vm = doc.querySelector('cr-ver-mas');
      const g = vm && vm.parentElement ? vm.parentElement.querySelector(GRID) : doc.querySelector(GRID);
      if (vm) this.total = +vm.dataset.total || this.total;
      return g ? [...g.children] : [];
    }

    _sep(page) {
      const d = document.createElement('div');
      d.className = 'cr-vm-sep'; d.setAttribute('role', 'separator');
      const from = (page - 1) * this.per + 1, to = Math.min(page * this.per, this.total);
      d.textContent = `Piezas ${from} a ${to}`;
      return d;
    }

    _insert(nodes, before) {
      const frag = document.createDocumentFragment();
      nodes.forEach(n => frag.appendChild(document.importNode(n, true)));
      // Scripts no se ejecutan al insertar: cargar los que falten (p. ej. product-card.js de swatches).
      frag.querySelectorAll('script').forEach(s => {
        if (s.src && document.querySelector(`script[src="${s.getAttribute('src')}"]`)) { s.remove(); return; }
        const n = document.createElement('script');
        [...s.attributes].forEach(a => n.setAttribute(a.name, a.value)); n.textContent = s.textContent;
        s.replaceWith(n);
      });
      this.grid.insertBefore(frag, before || null);
      document.dispatchEvent(new CustomEvent('cr:cards-added', { detail: { grid: this.grid } }));
      // Webkul subasta y otros: re-escaneo si existe API global
      try { window.wkAuctionInit && window.wkAuctionInit(); } catch (e) {}
    }

    _update() {
      const shown = Math.min(this.page * this.per, this.total);
      const last = this.page >= this.pages;
      this.text.textContent = last ? `Viste las ${this.total} piezas` : `Viendo ${shown} de ${this.total} piezas`;
      this.bar.style.width = (last ? 100 : (shown / this.total) * 100) + '%';
      if (this.btn) {
        if (last) this.btn.hidden = true;
        else this.btn.href = this._url(this.page + 1).pathname + this._url(this.page + 1).search;
      }
    }

    async loadNext() {
      if (this.busy || this.page >= this.pages) return;
      this.busy = true; this.btn.setAttribute('aria-busy', 'true');
      try {
        const next = this.page + 1;
        const nodes = await this._fetchCards(next);
        this.grid.appendChild(this._sep(next));
        this._insert(nodes);
        this.page = next;
        history.replaceState(history.state, '', this._url(next).toString());
        this._update();
      } catch (e) {
        location.href = this.btn.href; // fallback: navegación normal
      } finally { this.busy = false; this.btn && this.btn.removeAttribute('aria-busy'); }
    }

    _remember(e) {
      const a = e.target.closest('a[href]'); if (!a) return;
      const card = this.cards().find(c => c.contains(a)); if (!card) return;
      const idx = this.cards().indexOf(card); // índice absoluto (incluye páginas previas cargadas)
      try {
        sessionStorage.setItem(STORE, JSON.stringify({ key: keyFor(location.href), page: this.page, idx, top: card.getBoundingClientRect().top, t: Date.now() }));
      } catch (_) {}
    }

    async _restore() {
      // Página N>1 en la URL: completar 1..N-1 arriba para que la lista sea continua.
      let st = null;
      try { st = JSON.parse(sessionStorage.getItem(STORE) || 'null'); } catch (_) {}
      if (st && (st.key !== keyFor(location.href) || st.page !== this.firstPage || Date.now() - st.t > 36e5)) st = null;
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      const anchor = this.cards()[0];
      const anchorTop = anchor ? anchor.getBoundingClientRect().top : 0;
      try {
        const pages = [];
        for (let p = 1; p < this.firstPage; p++) pages.push(p);
        const lists = await Promise.all(pages.map(p => this._fetchCards(p)));
        lists.forEach((nodes, i) => {
          if (i > 0) this.grid.insertBefore(this._sep(i + 1), anchor);
          this._insert(nodes, anchor);
        });
        this.grid.insertBefore(this._sep(this.firstPage), anchor);
      } catch (e) { return; }
      const target = st ? this.cards()[st.idx] : anchor;
      const off = st ? st.top : (anchor ? Math.max(anchorTop, 0) : 0);
      if (target) {
        const y = target.getBoundingClientRect().top + window.scrollY - off;
        window.scrollTo({ top: Math.max(y, 0), behavior: 'auto' });
      }
      try { sessionStorage.removeItem(STORE); } catch (_) {}
    }

  }

  customElements.define('cr-ver-mas', CrVerMas);
})();
